import dayjs from 'dayjs';
import { Theme } from '@mui/material';

// SCENARIO LABELS ----------------------------------------------------------- //

export const SCENARIO_LABELS: Record<string, string> = {
  'concierge-pu': 'Valet Pickup',
  'concierge-ret': 'Valet Return',
  'concierge-loaner-pu': 'Valet Loaner Pickup',
  'concierge-loaner-ret': 'Valet Loaner Return',
  'round-trip': 'Round Trip',
  'round-trip-flex': 'Round Trip Flex',
  'round-trip-rideshare-after': 'Round Trip + Rideshare After',
  'round-trip-rideshare-before': 'Round Trip + Rideshare Before',
  'round-trip-rideshare-both': 'Round Trip + Rideshare Both',
  'round-trip-flex-rideshare-after': 'Round Trip Flex + Rideshare After',
  'round-trip-flex-rideshare-before': 'Round Trip Flex + Rideshare Before',
  'round-trip-flex-rideshare-both': 'Round Trip Flex + Rideshare Both',
  'stranded-no-rideshare': 'Stranded',
  'stranded-rideshare-after': 'Stranded + Rideshare After',
  'stranded-rideshare-before': 'Stranded + Rideshare Before',
  'stranded-rideshare-both': 'Stranded + Rideshare Both',
  'stranded-shuttle-after': 'Stranded + Shuttle After',
  'base': 'Base',
  'triangle': 'Triangle',
  'shuttle-drive': 'Shuttle Drive',
  'shuttle-linker': 'Shuttle Linker',
};

export const formatScenarioKey = (key: string): string => {
  if (SCENARIO_LABELS[key]) return SCENARIO_LABELS[key];
  const label = key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return label.replace(/Concierge/g, 'Valet');
};

// COMPASS ------------------------------------------------------------------- //

const COMPASS_DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** Convert heading degrees to compass direction string */
export const headingToCompass = (heading?: number): string => {
  if (heading == null) return '';
  return COMPASS_DIRS[Math.round(heading / 45) % 8];
};

// TELEMETRY FORMATTERS ------------------------------------------------------ //

export const formatSpeed = (speedMs?: number): string => {
  if (speedMs == null || speedMs < 0) return '-';
  const mph = speedMs * 2.237; // m/s to mph
  return `${Math.round(mph)} mph`;
};

export const formatHeading = (heading?: number): string => {
  if (heading == null) return '-';
  const compass = headingToCompass(heading);
  return `${compass} (${Math.round(heading)}°)`;
};

export const formatBattery = (level?: number, charging?: boolean): string => {
  if (level == null) return '-';
  const pct = Math.round(level * 100);
  return `${pct}%${charging ? ' (charging)' : ''}`;
};

/**
 * Format GPS accuracy.
 * - `'qualitative'`: returns "Excellent" / "Good" / "Fair" / "~Nm (weak)"
 * - `'feet'`: returns "~N ft"
 */
export const formatAccuracy = (accuracy?: number, format: 'qualitative' | 'feet' = 'feet'): string => {
  if (accuracy == null) return '-';
  if (format === 'qualitative') {
    if (accuracy <= 5) return 'Excellent';
    if (accuracy <= 15) return 'Good';
    if (accuracy <= 30) return 'Fair';
    return `~${Math.round(accuracy)}m (weak)`;
  }
  const ft = Math.round(accuracy * 3.281); // meters to feet
  return `~${ft} ft`;
};

// STATUS FORMATTERS --------------------------------------------------------- //

export const getStatusLabel = (status?: string) => {
  if (!status) return 'Unknown';
  return status.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export const getStatusColor = (status?: string, theme?: Theme) => {
  if (!status) return theme?.palette?.text?.disabled;
  if (status === 'delivery successful') return theme?.palette?.success?.main;
  if (status.includes('started') || status.includes('arrived')) return theme?.palette?.info?.main;
  if (status === 'pickup successful') return theme?.palette?.info?.main;
  if (status.includes('cancel') || status.includes('fail')) return theme?.palette?.error?.main;
  return theme?.palette?.warning?.main;
};

// TIME FORMATTER ------------------------------------------------------------ //

export const formatTime = (time?: string) => {
  if (!time) return '-';
  return dayjs(time).format('h:mm A');
};
